import CryptoJS from 'crypto-js';

// Production-level secure token storage
class SecureTokenStorage {
  constructor() {
    this.tokenKey = 'memic_admin_secure_token';
    this.userKey = 'memic_admin_secure_user';
    this.expiryKey = 'memic_admin_token_expiry';
    this.refreshKey = 'memic_admin_refresh_token';
    
    // Generate a unique encryption key based on browser fingerprint and timestamp
    this.encryptionKey = this.generateEncryptionKey();
    
    // Token expires after 24 hours by default
    this.defaultTokenExpiry = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
  }

  // Generate a secure encryption key
  generateEncryptionKey() {
    const browserFingerprint = this.getBrowserFingerprint();
    const sessionSalt = sessionStorage.getItem('memic_session_salt') || this.generateSessionSalt();
    return CryptoJS.SHA256(browserFingerprint + sessionSalt).toString();
  }

  // Generate browser fingerprint for additional security
  getBrowserFingerprint() {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillText('Memic Security', 2, 2);
    
    const fingerprint = [
      navigator.userAgent,
      navigator.language,
      screen.width + 'x' + screen.height,
      new Date().getTimezoneOffset(),
      canvas.toDataURL()
    ].join('|');
    
    return CryptoJS.SHA256(fingerprint).toString();
  }

  // Generate session salt
  generateSessionSalt() {
    const salt = CryptoJS.lib.WordArray.random(256/8).toString();
    sessionStorage.setItem('memic_session_salt', salt);
    return salt;
  }

  // Encrypt data before storage
  encrypt(data) {
    try {
      const encrypted = CryptoJS.AES.encrypt(JSON.stringify(data), this.encryptionKey).toString();
      return encrypted;
    } catch (error) {
      console.error('Encryption failed:', error);
      return null;
    }
  }

  // Decrypt data after retrieval
  decrypt(encryptedData) {
    try {
      const decrypted = CryptoJS.AES.decrypt(encryptedData, this.encryptionKey);
      const decryptedString = decrypted.toString(CryptoJS.enc.Utf8);
      return JSON.parse(decryptedString);
    } catch (error) {
      console.error('Decryption failed:', error);
      return null;
    }
  }

  // Store API token securely with expiration
  setApiToken(token, customExpiry = null) {
    try {
      const expiryTime = customExpiry || (Date.now() + this.defaultTokenExpiry);
      
      const tokenData = {
        token: token,
        timestamp: Date.now(),
        expiry: expiryTime,
        fingerprint: this.getBrowserFingerprint()
      };

      const encryptedToken = this.encrypt(tokenData);
      
      if (encryptedToken) {
        sessionStorage.setItem(this.tokenKey, encryptedToken);
        sessionStorage.setItem(this.expiryKey, expiryTime.toString());
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Failed to store secure API token:', error);
      return false;
    }
  }

  // Retrieve and validate API token
  getApiToken() {
    try {
      const encryptedToken = sessionStorage.getItem(this.tokenKey);
      const expiryTime = sessionStorage.getItem(this.expiryKey);
      
      if (!encryptedToken || !expiryTime) {
        return null;
      }

      // Check if token has expired
      if (Date.now() > parseInt(expiryTime)) {
        this.clearAll();
        return null;
      }

      const tokenData = this.decrypt(encryptedToken);
      
      if (!tokenData) {
        this.clearAll();
        return null;
      }

      // Validate browser fingerprint to prevent token theft
      if (tokenData.fingerprint !== this.getBrowserFingerprint()) {
        console.warn('Security violation: Browser fingerprint mismatch');
        this.clearAll();
        return null;
      }

      return tokenData.token;
    } catch (error) {
      console.error('Failed to retrieve secure API token:', error);
      this.clearAll();
      return null;
    }
  }

  // Store user data securely
  setUserData(userData) {
    try {
      const secureUserData = {
        ...userData,
        timestamp: Date.now(),
        fingerprint: this.getBrowserFingerprint()
      };

      const encryptedData = this.encrypt(secureUserData);
      
      if (encryptedData) {
        sessionStorage.setItem(this.userKey, encryptedData);
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Failed to store secure user data:', error);
      return false;
    }
  }

  // Retrieve user data securely
  getUserData() {
    try {
      const encryptedData = sessionStorage.getItem(this.userKey);
      
      if (!encryptedData) {
        return null;
      }

      const userData = this.decrypt(encryptedData);
      
      if (!userData) {
        return null;
      }

      // Validate browser fingerprint
      if (userData.fingerprint !== this.getBrowserFingerprint()) {
        console.warn('Security violation: User data fingerprint mismatch');
        this.clearAll();
        return null;
      }

      // Remove security metadata before returning
      const { fingerprint, timestamp, ...cleanUserData } = userData;
      return cleanUserData;
    } catch (error) {
      console.error('Failed to retrieve secure user data:', error);
      return null;
    }
  }

  // Store refresh token
  setRefreshToken(refreshToken) {
    try {
      const refreshData = {
        token: refreshToken,
        timestamp: Date.now(),
        fingerprint: this.getBrowserFingerprint()
      };

      const encryptedRefresh = this.encrypt(refreshData);
      
      if (encryptedRefresh) {
        sessionStorage.setItem(this.refreshKey, encryptedRefresh);
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Failed to store refresh token:', error);
      return false;
    }
  }

  // Get refresh token
  getRefreshToken() {
    try {
      const encryptedRefresh = sessionStorage.getItem(this.refreshKey);
      
      if (!encryptedRefresh) {
        return null;
      }

      const refreshData = this.decrypt(encryptedRefresh);
      
      if (!refreshData || refreshData.fingerprint !== this.getBrowserFingerprint()) {
        this.clearAll();
        return null;
      }

      return refreshData.token;
    } catch (error) {
      console.error('Failed to retrieve refresh token:', error);
      return null;
    }
  }

  // Clear all stored data securely
  clearAll() {
    try {
      // Overwrite data with random values before removing
      const keys = [this.tokenKey, this.userKey, this.expiryKey, this.refreshKey, 'memic_session_salt'];
      
      keys.forEach(key => {
        const randomData = CryptoJS.lib.WordArray.random(256/8).toString();
        sessionStorage.setItem(key, randomData);
        sessionStorage.removeItem(key);
      });

      return true;
    } catch (error) {
      console.error('Failed to clear stored data:', error);
      return false;
    }
  }

  // Check if token exists and is valid
  hasValidToken() {
    const token = this.getApiToken();
    return token && token.length > 0;
  }

  // Get time until token expires (in milliseconds)
  getTokenTimeToExpiry() {
    try {
      const expiryTime = sessionStorage.getItem(this.expiryKey);
      if (!expiryTime) return 0;
      
      const timeLeft = parseInt(expiryTime) - Date.now();
      return Math.max(0, timeLeft);
    } catch (error) {
      return 0;
    }
  }

  // Check if token needs refresh (expires within 1 hour)
  needsRefresh() {
    const timeLeft = this.getTokenTimeToExpiry();
    const oneHour = 60 * 60 * 1000;
    return timeLeft > 0 && timeLeft < oneHour;
  }

  // Get authorization header for API requests
  getAuthHeader() {
    const token = this.getApiToken();
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  }

  // Get security headers for requests
  getSecurityHeaders() {
    return {
      'X-Requested-With': 'XMLHttpRequest',
      'X-Session-Fingerprint': this.getBrowserFingerprint(),
      'X-Timestamp': Date.now().toString(),
      ...this.getAuthHeader()
    };
  }
}

export const secureTokenStorage = new SecureTokenStorage();
export default secureTokenStorage; 